import { CcdaCommunicationRequest } from '@seen/core/models/communicationRequests/CcdaCommunicationRequest'
import { Participant } from '@seen/core/models/patients/participant'
import {
  DataTable,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  PageLayout,
  ParticipantNameplate,
  RelativeDateTimeDisplay,
  Spinner,
  useNavigateFromClickEvent,
  useSearch,
  useSuspenseSearch,
} from '@seen/react'
import { Button } from '@seen/ui'
import type { ColumnDef } from '@tanstack/react-table'
import { orderBy } from 'lodash-es'
import {
  type JSX,
  type MouseEvent,
  useCallback,
  useMemo,
  useState,
} from 'react'
import { useCcdaExport } from '#src/pages/ccda/hooks/useCcdaExport.ts'

export function CcdaPage(): JSX.Element {
  const { exportCcda } = useCcdaExport()
  const [exportingId, setExportingId] = useState<string | null>(null)

  const { data: participants } = useSuspenseSearch(
    Participant,
    Participant.enrolledSearchQuery()
  )

  const handleExport = useCallback(
    async (participant: Participant): Promise<void> => {
      setExportingId(participant.id as string)
      try {
        await exportCcda(participant)
      } finally {
        setExportingId(null)
      }
    },
    [exportCcda]
  )

  const columns = useMemo<ColumnDef<Participant>[]>(
    () => [
      {
        id: 'participant',
        header: 'Participant',
        cell: ({ row }) => (
          <ParticipantNameplate participant={row.original.reference} />
        ),
      },
      {
        id: 'seenMRN',
        header: 'MRN',
        cell: ({ row }) => <span>{row.original.seenMRN ?? '-'}</span>,
      },
      {
        id: 'export',
        header: 'Export CCDA',
        cell: ({ row }) => (
          <Button
            size="sm"
            onClick={(e: MouseEvent) => {
              e.stopPropagation()
              void handleExport(row.original)
            }}
            disabled={exportingId === row.original.id}
          >
            {exportingId === row.original.id ? 'Exporting...' : 'Export'}
          </Button>
        ),
      },
      {
        id: 'existing',
        header: 'Existing Exports',
        cell: ({ row }) => <ExistingExportsCell participant={row.original} />,
      },
    ],
    [exportingId, handleExport]
  )

  const sortedParticipants = orderBy(
    participants ?? [],
    [(p) => p.lastName?.toLowerCase(), (p) => p.firstName?.toLowerCase()],
    ['asc', 'asc']
  )

  return (
    <PageLayout title="CCDA Exports">
      <DataTable columns={columns} data={sortedParticipants} />
    </PageLayout>
  )
}

function ExistingExportsCell({
  participant,
}: {
  participant: Participant
}): JSX.Element {
  const navigate = useNavigateFromClickEvent()
  const {
    data: exports,
    isLoading,
    error,
  } = useSearch(
    CcdaCommunicationRequest,
    CcdaCommunicationRequest.forPatient(participant.id as string)
  )

  if (isLoading) {
    return <Spinner className="size-4" />
  }

  if (error) {
    return <span className="text-destructive">Error</span>
  }

  const sortedExports = orderBy(exports ?? [], [(e) => e.exportDate], ['desc'])
  const count = sortedExports.length

  if (count === 0) {
    return <span className="text-muted-foreground">None</span>
  }

  if (count === 1) {
    return (
      <Button
        size="sm"
        variant="ghost"
        onClick={(e: MouseEvent) => {
          e.stopPropagation()
          navigate(`/ccda/${participant.id}/export/${sortedExports[0].id}`, e)
        }}
      >
        Export -{' '}
        {sortedExports[0].exportDate ? (
          <RelativeDateTimeDisplay
            value={sortedExports[0].exportDate}
            element="span"
          />
        ) : (
          'Unknown date'
        )}
      </Button>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          size="sm"
          variant="ghost"
          onClick={(e: MouseEvent) => {
            e.stopPropagation()
          }}
        >
          {count} exports
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {sortedExports.map((exp) => (
          <DropdownMenuItem
            key={exp.id}
            onClick={(e: MouseEvent) => {
              e.stopPropagation()
              navigate(`/ccda/${participant.id}/export/${exp.id}`, e)
            }}
          >
            {exp.exportDate ? (
              <RelativeDateTimeDisplay value={exp.exportDate} element="span" />
            ) : (
              'Unknown date'
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
